import { useMemo, useState } from "react";
import { FieldValues, UseFormReturn } from "react-hook-form";
import styled from "styled-components";
import { z } from "zod";
import { SP_BREAKPOINT } from "../../../constants/app";
import { Client } from "../../../ducks/client";
import { Group } from "../../../ducks/group";
import { Button } from "../../atoms/Button";
import { Input } from "../../atoms/Input";
import { Select, SelectOptionType } from "../../atoms/Select";
import FormTemplate from "../../templates/FormTemplate";

type Props = {
  group: Group;
  clients: Client[];
  onCancel: () => void;
  handleSubmit: (customerId: string, name: string) => void;
};

export const GroupEditForm: React.FC<Props> = ({
  group,
  clients,
  onCancel,
  handleSubmit,
}) => {
  const schema = useMemo(() => {
    return z.object({
      customerId: z.string().min(1),
      name: z.string().min(1),
    });
  }, []);

  const [methods, setMethods] = useState<UseFormReturn<
    FieldValues,
    unknown,
    FieldValues
  > | null>(null);

  const clientOptions: SelectOptionType[] = useMemo(() => {
    return clients.map((client, i) => ({
      id: i,
      name: client.name,
      value: client.id,
    }));
  }, [clients]);

  const defaultClientId = useMemo(() => {
    return clients.find((client) => client.name === group.clientName)?.id;
  }, [clients, group]);

  const defaultClientName = useMemo(() => {
    return clients.find((client) => client.name === group.clientName)?.name;
  }, [clients, group]);

  return (
    <FormTemplate
      schema={schema}
      setMethods={setMethods}
      defaultValues={{
        customerId: defaultClientId,
        name: group.name,
      }}
      onSubmit={({ customerId, name }) => handleSubmit(customerId, name)}
    >
      <StyledContainer>
        <StyledInputContainer>
          <Select
            name="customerId"
            label="顧客名"
            options={clientOptions}
            defaultValue={defaultClientName}
            onChange={() => {}}
            width="100%"
            required
          />

          <Input label="グループ名" name="name" maxLength={255} required />
        </StyledInputContainer>

        <StyledFooter>
          <Button
            text="キャンセル"
            variant="dark"
            type="button"
            onClick={onCancel}
          />
          <Button text="更新" variant="filled" type="submit" />
        </StyledFooter>
      </StyledContainer>
    </FormTemplate>
  );
};

const StyledContainer = styled.div`
  @media (max-width: ${SP_BREAKPOINT}) {
    padding: 0;
    margin-top: 24px;
  }

  padding: 0 10%;
`;

const StyledInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-bottom: 24px;
`;

const StyledFooter = styled.div`
  @media (max-width: ${SP_BREAKPOINT}) {
    justify-content: center;

    button {
      width: 100%;
    }
  }

  display: flex;
  justify-content: flex-end;
  gap: 16px;
  padding: 32px 0;
  border-top: 1px solid #e6e6e6;
`;
